import { useEffect, useState } from "react";
import FormField from "../../FormField";
import { DoNotCare } from "../../../types";
import Radio from "..";
import { RadioGroupProps } from "./types";
import { styles } from "./styles";
import ReadonlyFormField from "../../FormField/Readonly";

/**
 * `RadioGroup` renders a group of radio buttons, and allows users to select one option from a set.
 * It manages the selected state of its child radio buttons, ensuring that only one radio button in the group can be selected at a time.
 */
export const RadioGroup = <
  Option extends DoNotCare,
  Selected extends DoNotCare
>({
  disabled = false,
  fill = false,
  stacked = false,
  variant = "default",
  label,
  description,
  hideLabel,
  error,
  offsetError,
  required,
  labelSuffix,
  labelSuffixAlign,
  readonly,
  readonlyParser,
  options,
  value,
  lookupLabel,
  lookupValue,
  onChange
}: RadioGroupProps<Option, Selected>) => {
  const [groupValue, setGroupValue] = useState<Selected>(value);

  useEffect(() => {
    setGroupValue(value);
  }, [value]);

  const lookupCleanValue = (option: Option) => {
    const optionValue = lookupValue(option);
    if (
      optionValue === undefined ||
      (typeof optionValue !== "string" && typeof optionValue !== "number")
    ) {
      return undefined;
    }
    return optionValue;
  };

  const handleOnChange = (option: Option) => {
    const optionValue = lookupCleanValue(option);
    if (optionValue === groupValue || optionValue === undefined) {
      return;
    }
    setGroupValue(optionValue);
    if (onChange) {
      onChange(optionValue);
    }
  };

  if (readonly) {
    return (
      <ReadonlyFormField
        parser={readonlyParser || lookupLabel}
        fill={fill}
        label={label}
        hideLabel={hideLabel}
        value={value}
        description={description}
        labelSuffix={labelSuffix}
        labelSuffixAlign={labelSuffixAlign}
        error={error}
        offsetError={offsetError}
      />
    );
  }

  return (
    <FormField
      fieldset
      label={label}
      error={error}
      offsetError={offsetError}
      fill={fill}
      required={required}
      description={description}
      hideLabel={hideLabel}
      labelSuffix={labelSuffix}
      labelSuffixAlign={labelSuffixAlign}
    >
      <div
        data-testid="kit-RadioGroup"
        className="radio-group"
        css={[
          styles.base,
          variant === "chip" && styles.chipGroup,
          fill && styles.fill,
          stacked && styles.stacked,
          label && styles.withLabel,
          !!error && !!options.length && styles.error
        ]}
      >
        {options.map((option: Option) => (
          <Radio
            key={lookupCleanValue(option)}
            disabled={disabled}
            value={lookupCleanValue(option) || ""}
            label={lookupLabel(option)}
            onChange={() => handleOnChange(option)}
            checked={groupValue === lookupValue(option)}
            variant={variant}
          />
        ))}
      </div>
    </FormField>
  );
};

export default RadioGroup;
export * from "./types";
